import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { UserData } from "../../types/user-data.type";
import useApi from "../../hooks/api/useApi";
import { Group } from "../../types/group.type";

export default function AdminGroupForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const user = localStorage.getItem("user");
  const userData: UserData = JSON.parse(user ?? "");
  const { request } = useApi();

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.authToken,
  };

  useEffect(() => {
    if (!isEdit) return;
    setIsFetching(true);
    request(
      `/groups/${id}`,
      { method: "Get", headers: authHeaders },
      (result) => {
        const group: Group = result.payload;
        setName(group.name);
        setDescription(group.description);
        setIsFetching(false);
      }
    ).catch(() => setIsFetching(false));
  }, [id]);

  const validate = () => {
    const errs: typeof errors = {};
    if (!name.trim()) errs.name = "نام گروه الزامی است.";
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setIsLoading(true);

    const body = JSON.stringify({ name: name.trim(), description: description.trim() });
    const url = isEdit ? `/groups/${id}` : "/groups";
    const method = isEdit ? "Put" : "Post";

    try {
      await request(
        url,
        { method, headers: authHeaders, body },
        () => navigate("/admin/groups")
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        در حال بارگذاری...
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-800 font-sans">
      <div className="mx-auto min-h-screen shadow-2xl relative overflow-hidden">
        <main className="h-full">
          <div className="p-6 space-y-6 max-w-lg">
            {/* Header */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/admin/groups")}
                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
              >
                <ArrowRight className="w-5 h-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-800">
                {isEdit ? "ویرایش گروه" : "ایجاد گروه جدید"}
              </h1>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  نام <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="نام گروه را وارد کنید"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? "border-red-400" : "border-gray-300"
                  }`}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  توضیحات
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="توضیحات گروه را وارد کنید"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "در حال ذخیره..." : isEdit ? "ذخیره تغییرات" : "ایجاد گروه"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/groups")}
                  className="px-6 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
